import { EmploymentField, useEmploymentSelection } from './EmploymentField'
import { useState } from 'react'
import { Text, XStack, YStack } from 'tamagui'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { useApp, type Localized } from '../../../i18n'
import { api } from '../../../api/client'
import { tone } from '../../../theme/tones'
import { Button, Chip, DateField, FormInput, FormTextArea, Icon } from '../../../ui'
import { DATE_RE, plusYearsYmd } from '../../../lib/date'
import { daysInclusive, fmtNum } from '../../../lib/format'
import { pickFileWeb } from '../../../lib/files'
import type { EdoType, Profile, VacationKind } from '../../../lib/types'
import { useMe } from '../../users/useUsers'
import { useCreateVacation, useVacationBalance } from '../useEdo'
import { VAC_KIND } from '../processVM'
import { RoutePreview } from '../components/RouteTimeline'
import { DateRange, Field, FormActions, MISSING_DATE_FROM, MISSING_DATE_TO, MISSING_WORKPLACE, dateCol, isWeb } from './shared'
import { OnBehalfField } from './OnBehalfField'
import { PersonPickField } from './PersonPickField'

/**
 * Виды отпуска в селекторе формы. Первые три уходят в POST /edo/vacation как `kind`, «семейные»
 * (ТЗ-2026-012/013/014) — в собственные ручки со своим payload, поэтому union локальный:
 * `VacationKind` в types.ts зеркалит payload бэкенда и расширять его нельзя.
 */
type LeaveFormKind = VacationKind | 'paternity' | 'maternity' | 'childcare'
const LEAVE_KINDS: LeaveFormKind[] = ['basic', 'unpaid', 'study', 'paternity', 'maternity', 'childcare']
/** Метки чипов: базовые — из VAC_KIND (общие со списком заявок), «семейные» — из бывших плиток. */
const LEAVE_KIND: Record<string, Localized> = {
  ...VAC_KIND,
  paternity: { ru: 'Отцовский отпуск', en: 'Paternity leave' },
  maternity: { ru: 'Материнский отпуск', en: 'Maternity leave' },
  childcare: { ru: 'Уход за ребёнком', en: 'Child-care leave' },
}
/**
 * Вид отпуска → тип заявки ЭДО. Форма одна, а процессы разные: у «семейных» видов свои ручки
 * и свои маршруты (без согласования руководителем), поэтому предпросмотр маршрута смотрит сюда.
 * basic/unpaid/study уходят одной ручкой POST /edo/vacation — маршрут у них общий.
 */
const LEAVE_EDO_TYPE: Record<LeaveFormKind, EdoType> = {
  basic: 'VACATION',
  unpaid: 'VACATION',
  study: 'VACATION',
  paternity: 'PATERNITY_LEAVE',
  maternity: 'MATERNITY_LEAVE',
  childcare: 'CHILD_CARE_LEAVE',
}

/**
 * Заявка на отпуск — одна форма на шесть видов: выбранный тип открывает свои поля (справка из
 * клиники у отцовского, данные ребёнка у ухода) и определяет ручку отправки.
 */
export function VacationForm({ onDone }: { onDone: () => void }) {
  const { t } = useApp()
  const qc = useQueryClient()
  const [kind, setKind] = useState<LeaveFormKind>('basic')
  const [startDate, setStart] = useState('')
  const [endDate, setEnd] = useState('')
  const [comment, setComment] = useState('')
  // Поля «семейных» видов: справка (отцовский) и данные ребёнка (уход за ребёнком).
  const [file, setFile] = useState<File | null>(null)
  const [lastName, setLastName] = useState('')
  const [firstName, setFirstName] = useState('')
  const [middleName, setMiddleName] = useState('')
  const [birthDate, setBirthDate] = useState('')
  // Подача «от имени» (роль proxy): выбранный сотрудник станет инициатором заявки на бэке.
  const [onBehalf, setOnBehalf] = useState<Profile | null>(null)
  // Замещающий на время отпуска (только basic/unpaid/study). Обязательность решает сервер:
  // роль в юрлице заявки или подчинённые — это знает он, его текст отказа покажет errorText.
  const [deputy, setDeputy] = useState<Profile | null>(null)
  const me = useMe()
  const employment = useEmploymentSelection(onBehalf?.id)
  const isFamily = kind === 'paternity' || kind === 'maternity' || kind === 'childcare'

  const create = useCreateVacation(onDone)
  /** У «семейных» видов своя ручка и свой payload; отцовский сперва грузит справку как edo_doc. */
  const createFamily = useMutation({
    mutationFn: async () => {
      const onBehalfOfUserId = onBehalf?.id
      if (kind === 'paternity') {
        const form = new FormData()
        form.append('file', file as unknown as Blob)
        const up = await api.uploadEdoDoc(form)
        return api.createPaternityLeave({
          dateFrom: startDate,
          dateTo: endDate,
          uploadId: up.id,
          comment: comment.trim() || undefined,
          onBehalfOfUserId,
          employmentId: employment.employmentId,
        })
      }
      if (kind === 'maternity') {
        return api.createMaternityLeave({
          dateFrom: startDate,
          dateTo: endDate,
          comment: comment.trim() || undefined,
          onBehalfOfUserId,
          employmentId: employment.employmentId,
        })
      }
      return api.createChildCareLeave({
        dateFrom: startDate,
        dateTo: endDate || undefined,
        child: {
          lastName: lastName.trim(),
          firstName: firstName.trim(),
          middleName: middleName.trim() || null,
          birthDate,
        },
        comment: comment.trim() || undefined,
        onBehalfOfUserId,
          employmentId: employment.employmentId,
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['edo-requests'] })
      onDone()
    },
  })
  const m = isFamily ? createFamily : create

  // Остаток отпускных дней из 1С на дату начала (ФТ-01/КП-01) — «графа остатка».
  // «Семейные» виды остатка не знают: они не расходуют отпускные дни.
  // Остаток относится к выбранному сотруднику и его месту работы.
  const asOf = DATE_RE.test(startDate) ? startDate : undefined
  const balanceQ = useVacationBalance(asOf, employment.employmentId, onBehalf?.id, employment.ready)
  const balance = balanceQ.data
  const balanceDays =
    !isFamily && balance?.source === 'zup' && balance.balanceDays != null
      ? balance.balanceDays
      : null

  // Уход за ребёнком: «По (плановая)» необязательна — пустая дата означает бессрочный уход.
  const datesOk =
    DATE_RE.test(startDate) &&
    (kind === 'childcare' ? !endDate || endDate >= startDate : DATE_RE.test(endDate) && endDate >= startDate)
  const childOk = lastName.trim() !== '' && firstName.trim() !== '' && DATE_RE.test(birthDate)
  const duration = datesOk && DATE_RE.test(endDate) ? daysInclusive(startDate, endDate) : 0
  // ТЗ 1.5/Q9: разрешено запрашивать не больше 5 дней сверх остатка (только оплачиваемый basic).
  const overBalance =
    kind === 'basic' && balanceDays != null && duration > 0 && duration > balanceDays + 5

  // Отцовский без справки бэк вернёт 400 (ТЗ-2026-014 §2.3) — блокируем кнопку заранее.
  // Пустые обязательные — в порядке экрана (данные ребёнка стоят выше дат, справка — ниже).
  const childcare = kind === 'childcare'
  const missing = [
    !employment.ready && t(MISSING_WORKPLACE),
    childcare && !lastName.trim() && t({ ru: 'Фамилия ребёнка', en: "Child's last name" }),
    childcare && !firstName.trim() && t({ ru: 'Имя ребёнка', en: "Child's first name" }),
    childcare && !DATE_RE.test(birthDate) && t({ ru: 'Дата рождения ребёнка', en: "Child's date of birth" }),
    !DATE_RE.test(startDate) && t(MISSING_DATE_FROM),
    !childcare && !DATE_RE.test(endDate) && t(MISSING_DATE_TO),
    kind === 'paternity' && !file && t({ ru: 'Справка из клиники', en: 'Clinic certificate' }),
  ].filter((x): x is string => !!x)
  const ok = missing.length === 0 && datesOk && !overBalance && !m.isPending
  const submit = () => {
    if (!ok) return
    if (isFamily) createFamily.mutate()
    else
      create.mutate({
        kind,
        startDate,
        endDate,
        comment: comment.trim() || undefined,
        onBehalfOfUserId: onBehalf?.id,
        employmentId: employment.employmentId,
        deputyId: deputy?.id,
      })
  }

  return (
    <YStack gap={14}>
      <OnBehalfField
        value={onBehalf}
        onChange={(p) => {
          setOnBehalf(p)
          // Подающий не может замещать сам себя: выбранного «от имени» убираем из замещающих.
          if (p && p.id === deputy?.id) setDeputy(null)
        }}
      />
      <EmploymentField selection={employment} />
      {/* графа остатка отпускных дней из 1С */}
      {balanceDays != null && (
        <XStack
          alignItems="center"
          gap={9}
          backgroundColor="$primaryBg"
          borderWidth={1}
          borderColor="$primary"
          borderRadius={7}
          paddingHorizontal={12}
          paddingVertical={9}
        >
          <Icon name="calendar" size={16} color="$primary" />
          <YStack flex={1}>
            <Text fontSize={13} fontWeight="700" color="$color">
              {t({
                ru: `Остаток отпускных дней: ${fmtNum(balanceDays, 2)}`,
                en: `Vacation days left: ${fmtNum(balanceDays, 2)}`,
                az: `Qalan məzuniyyət günləri: ${fmtNum(balanceDays, 2)}`,
              })}
            </Text>
            {balance!.byRight.length > 0 && (
              <Text fontSize={11} color="$muted">
                {balance!.byRight.map((b) => `${b.rightType}: ${fmtNum(b.balanceDays, 2)}`).join(' · ')}
                {' · '}
                {t({ ru: 'из 1С', en: 'from 1C' })}
              </Text>
            )}
          </YStack>
        </XStack>
      )}

      <Field label={t({ ru: 'Тип отпуска', en: 'Leave type' })} required>
        <XStack gap={8} flexWrap="wrap">
          {/* dim всегда: вид отпуска выбран изначально ('basic'), так что «пустой» группы не бывает. */}
          {LEAVE_KINDS.map((k) => (
            <Chip key={k} on={kind === k} dim onPress={() => setKind(k)}>
              {t(LEAVE_KIND[k])}
            </Chip>
          ))}
        </XStack>
      </Field>
      {kind === 'childcare' ? (
        <>
          <Field label={t({ ru: 'Фамилия ребёнка', en: "Child's last name" })} required>
            <FormInput value={lastName} onChangeText={setLastName} placeholder="Məmmədova" />
          </Field>
          <Field label={t({ ru: 'Имя ребёнка', en: "Child's first name" })} required>
            <FormInput value={firstName} onChangeText={setFirstName} placeholder="Aysel" />
          </Field>
          <Field label={t({ ru: 'Отчество (если есть)', en: 'Middle name (if any)' })}>
            <FormInput value={middleName} onChangeText={setMiddleName} />
          </Field>
          <YStack {...dateCol} gap={6}>
            <Field label={t({ ru: 'Дата рождения ребёнка', en: "Child's date of birth" })} required>
              <DateField
                value={birthDate}
                onChange={(d) => {
                  setBirthDate(d)
                  // Плановое окончание = +3 года (§3.2). Подставляем, пока пользователь не задал своё.
                  if (d && !endDate) setEnd(plusYearsYmd(d, 3))
                }}
                min="1950-01-01"
                placeholder="ГГГГ-ММ-ДД"
                highlight={!birthDate}
              />
            </Field>
          </YStack>
        </>
      ) : null}
      <DateRange
        from={startDate}
        to={endDate}
        setFrom={setStart}
        setTo={setEnd}
        toRequired={kind !== 'childcare'}
        toLabel={kind === 'childcare' ? t({ ru: 'По (плановая)', en: 'To (planned)' }) : undefined}
      />
      {kind === 'paternity' ? (
        <>
          <Field label={t({ ru: 'Справка из клиники', en: 'Clinic certificate' })} required>
            {isWeb ? (
              <Button
                variant="default"
                block
                icon={<Icon name="paperclip" size={15} color="$muted" />}
                onPress={async () => setFile(await pickFileWeb('.pdf,.jpg,.jpeg,.png'))}
              >
                {file
                  ? `📎 ${file.name}`
                  : t({ ru: 'Прикрепить справку (PDF/JPG/PNG)', en: 'Attach certificate (PDF/JPG/PNG)' })}
              </Button>
            ) : (
              <Text fontSize={12} color="$muted">
                {t({
                  ru: 'Прикрепление справки доступно в веб-версии портала',
                  en: 'Certificate upload is available in the web version',
                })}
              </Text>
            )}
          </Field>
          <Text fontSize={11} color="$muted">
            {t({
              ru: 'Без справки заявка не отправляется — её проверит HR.',
              en: 'The request cannot be submitted without the certificate — HR will verify it.',
            })}
          </Text>
        </>
      ) : null}
      {kind === 'maternity' ? (
        <Text fontSize={11} color="$muted">
          {t({
            ru: 'Данные больничного листа внесёт HR — прикладывать их к заявке не нужно.',
            en: 'HR fills in the medical sheet details — no need to attach them here.',
          })}
        </Text>
      ) : null}
      {kind === 'childcare' ? (
        <Text fontSize={11} color="$muted">
          {t({
            ru: 'Плановая дата — дата рождения + 3 года. Очистите её, если уход оформляется бессрочно.',
            en: 'The planned date is the birth date + 3 years. Clear it for an open-ended leave.',
          })}
        </Text>
      ) : null}
      {!isFamily ? (
        <PersonPickField
          label={{
            ru: 'Замещающий на время отпуска',
            en: 'Substitute during vacation',
            az: 'Məzuniyyət müddətində əvəz edən',
          }}
          hint={{
            ru: 'Обязательно для руководителей и участников согласований',
            en: 'Required for managers and approvers',
            az: 'Rəhbərlər və razılaşdırma iştirakçıları üçün məcburidir',
          }}
          value={deputy}
          onChange={setDeputy}
          excludeIds={[me.data?.id, onBehalf?.id].filter((x): x is string => !!x)}
        />
      ) : null}
      <Field label={t({ ru: 'Комментарий (необязательно)', en: 'Comment (optional)' })}>
        <FormTextArea
          value={comment}
          onChangeText={setComment}
          placeholder={t({ ru: 'Например: ежегодный отпуск', en: 'e.g. annual leave' })}
        />
      </Field>
      {duration > 0 && (
        <XStack alignItems="center" gap={7}>
          <Icon name="clock" size={14} color={overBalance ? tone('red').text : '$muted'} />
          <Text fontSize={12} fontWeight="600" color={overBalance ? tone('red').text : '$muted'}>
            {t({ ru: `Длительность: ${duration} дн.`, en: `Duration: ${duration} d`, az: `Müddət: ${duration} gün` })}
          </Text>
        </XStack>
      )}
      {overBalance && (
        <Text fontSize={12} color={tone('red').text}>
          {t({
            ru: `Запрошено ${duration} дн., остаток ${fmtNum(balanceDays!, 2)} — можно не больше остатка + 5 дней`,
            en: `Requested ${duration} d, balance ${fmtNum(balanceDays!, 2)} — max is balance + 5 days`,
            az: `Tələb olunan ${duration} gün, qalıq ${fmtNum(balanceDays!, 2)} — ən çoxu qalıq + 5 gün`,
          })}
        </Text>
      )}
      <RoutePreview type={LEAVE_EDO_TYPE[kind]} onBehalfOfUserId={onBehalf?.id} employmentId={employment.employmentId} enabled={employment.ready} />
      <FormActions onCancel={onDone} onSubmit={submit} ok={ok} m={m} missing={missing} />
    </YStack>
  )
}
